import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Mail, Send, RotateCcw, Eye, EyeOff, Code, X, Check } from 'lucide-react';

const EmailTemplatesTab = ({ authAxios }) => {
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [sendTestModal, setSendTestModal] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, [authAxios]);

  const fetchTemplates = async () => {
    try {
      const response = await authAxios.get('/admin/email-templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch email templates:', error);
    }
  };

  const handleSave = async (template) => {
    setSaving(true);
    try {
      await authAxios.put(`/admin/email-templates/${template.id}`, {
        name: template.name,
        description: template.description,
        subject: template.subject,
        html_body: template.html_body,
        enabled: template.enabled,
      });
      toast.success(`Template "${template.name}" saved!`);
      fetchTemplates();
      setEditingTemplate(null);
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (template) => {
    try {
      await authAxios.put(`/admin/email-templates/${template.id}`, {
        enabled: !template.enabled
      });
      toast.success(`${template.name} ${!template.enabled ? 'enabled' : 'disabled'}`);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to toggle template');
    }
  };

  const handleReset = async (templateId) => {
    if (!window.confirm('Reset this template to its default content? Your changes will be lost.')) return;
    try {
      await authAxios.post(`/admin/email-templates/reset/${templateId}`);
      toast.success('Template reset to default');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to reset template');
    }
  };

  const handleSendTest = async () => {
    if (!testEmail || !sendTestModal) return;
    setSending(true);
    try {
      const response = await authAxios.post(`/admin/email-templates/${sendTestModal.id}/send-test`, {
        recipient_email: testEmail
      });
      if (response.data.success) {
        toast.success(`Test email sent to ${testEmail}`);
        setSendTestModal(null);
        setTestEmail('');
      } else {
        toast.error(response.data.message || 'Failed to send');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setSending(false);
    }
  };

  const renderPreviewHtml = (template) => {
    if (!template) return '';
    let html = template.html_body || '';
    const sampleVars = {
      name: 'John Smith', email: 'john@example.com',
      service: 'Accommodation Solutions', reference_id: 'TEST-001',
      message: 'I am interested in your property management services for my portfolio of 3 properties in Edinburgh.'
    };
    for (const [key, val] of Object.entries(sampleVars)) {
      html = html.replaceAll(`{{${key}}}`, val);
    }
    return html;
  };

  return (
    <div className="space-y-6" data-testid="email-templates-tab">
      <div className="bg-[#111111] border border-white/5 p-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-mono font-bold text-lg flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#D4AF37]" />
            Email Templates
          </h2>
        </div>
        <p className="text-white/50 text-sm mb-6">
          Customize notification emails sent when enquiries are submitted. Use <code className="text-[#D4AF37] bg-[#D4AF37]/10 px-1.5 py-0.5 text-xs">{'{{variable}}'}</code> placeholders for dynamic content.
        </p>

        <div className="space-y-4">
          {templates.map(template => (
            <div key={template.id} className="bg-[#0A0A0A] border border-white/10 rounded overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/5">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-mono font-bold text-sm">{template.name}</h3>
                    <span className={`px-2 py-0.5 text-xs font-mono rounded ${
                      template.enabled 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-white/10 text-white/40'
                    }`}>
                      {template.enabled ? 'ACTIVE' : 'DISABLED'}
                    </span>
                  </div>
                  <p className="text-white/40 text-xs mt-1">{template.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggle(template)}
                    className={`p-2 transition-colors ${template.enabled ? 'text-green-400 hover:text-green-300' : 'text-white/30 hover:text-white/50'}`}
                    title={template.enabled ? 'Disable' : 'Enable'}
                    data-testid={`toggle-template-${template.id}`}
                  >
                    {template.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => setPreviewTemplate(template)}
                    className="p-2 text-white/50 hover:text-[#D4AF37] transition-colors"
                    title="Preview"
                    data-testid={`preview-template-${template.id}`}
                  >
                    <Code className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setSendTestModal(template)}
                    className="p-2 text-white/50 hover:text-[#D4AF37] transition-colors"
                    title="Send test email"
                    data-testid={`test-template-${template.id}`}
                  >
                    <Send className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setEditingTemplate({...template})}
                    className="px-3 py-1 bg-white/10 text-white hover:bg-white/20 font-mono text-xs transition-colors"
                    data-testid={`edit-template-${template.id}`}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleReset(template.id)}
                    className="p-2 text-white/30 hover:text-white/60 transition-colors"
                    title="Reset to default"
                    data-testid={`reset-template-${template.id}`}
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Subject preview */}
              <div className="px-4 py-3 flex items-center gap-2">
                <span className="text-white/30 text-xs font-mono uppercase">Subject:</span>
                <span className="text-white/70 text-sm">{template.subject}</span>
              </div>

              {/* Available variables */}
              <div className="px-4 pb-3 flex flex-wrap gap-2">
                <span className="text-white/30 text-xs font-mono">Variables:</span>
                {template.variables?.map(v => (
                  <code key={v} className="text-[#D4AF37]/70 bg-[#D4AF37]/5 px-1.5 py-0.5 text-xs font-mono">
                    {`{{${v}}}`}
                  </code>
                ))}
              </div>
            </div>
          ))}

          {templates.length === 0 && (
            <div className="text-center py-12 text-white/30 font-mono text-sm">
              Loading templates...
            </div>
          )}
        </div>
      </div>

      {/* Edit Template Modal */}
      {editingTemplate && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#111111] border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 sticky top-0 bg-[#111111] z-10">
              <h3 className="font-mono font-bold text-lg">Edit Template: {editingTemplate.name}</h3>
              <button onClick={() => setEditingTemplate(null)} className="p-1 text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-2">Template Name</label>
                <input
                  type="text"
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                  className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none"
                />
              </div>
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-2">Description</label>
                <input
                  type="text"
                  value={editingTemplate.description}
                  onChange={(e) => setEditingTemplate({...editingTemplate, description: e.target.value})}
                  className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none"
                />
              </div>
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-2">Email Subject</label>
                <input
                  type="text"
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                  className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none"
                />
                <p className="text-white/30 text-xs mt-1">Use {'{{name}}'}, {'{{service}}'}, etc. for dynamic values</p>
              </div>
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-2">Available Variables</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {editingTemplate.variables?.map(v => (
                    <button
                      key={v}
                      onClick={() => {
                        const textarea = document.getElementById('template-html-editor');
                        if (textarea) {
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const text = editingTemplate.html_body;
                          const newText = text.substring(0, start) + `{{${v}}}` + text.substring(end);
                          setEditingTemplate({...editingTemplate, html_body: newText});
                        }
                      }}
                      className="px-2 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-mono hover:bg-[#D4AF37]/20 transition-colors"
                    >
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-2">HTML Body</label>
                <textarea
                  id="template-html-editor"
                  value={editingTemplate.html_body}
                  onChange={(e) => setEditingTemplate({...editingTemplate, html_body: e.target.value})}
                  rows={16}
                  className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-xs focus:border-[#D4AF37] outline-none resize-y leading-relaxed"
                  spellCheck={false}
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => handleSave(editingTemplate)}
                  disabled={saving}
                  data-testid="save-email-template"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#D4AF37] text-black font-mono text-sm font-bold hover:bg-[#D4AF37]/90 transition-colors disabled:opacity-50"
                >
                  <Check className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Template'}
                </button>
                <button
                  onClick={() => setEditingTemplate(null)}
                  className="px-4 py-3 border border-white/20 text-white/70 font-mono text-sm hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#111111] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 sticky top-0 bg-[#111111] z-10">
              <div>
                <h3 className="font-mono font-bold">Preview: {previewTemplate.name}</h3>
                <p className="text-white/40 text-xs mt-1">Rendered with sample data</p>
              </div>
              <button onClick={() => setPreviewTemplate(null)} className="p-1 text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <div className="bg-white rounded overflow-hidden">
                <div
                  dangerouslySetInnerHTML={{ __html: renderPreviewHtml(previewTemplate) }}
                  style={{ minHeight: '200px' }}
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-white/5">
              <button
                onClick={() => { setPreviewTemplate(null); setSendTestModal(previewTemplate); }}
                className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-mono text-sm font-bold hover:bg-[#D4AF37]/90"
              >
                <Send className="w-4 h-4" /> Send Test Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Test Modal */}
      {sendTestModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#111111] border border-white/10 p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-mono font-bold text-lg flex items-center gap-2">
                  <Send className="w-5 h-5 text-[#D4AF37]" />
                  Send Test Email
                </h3>
                <p className="text-white/50 text-xs mt-1">Will use sample data for all variables</p>
              </div>
              <button onClick={() => { setSendTestModal(null); setTestEmail(''); }} className="p-1 text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="p-3 bg-[#0A0A0A] border border-white/10 rounded">
                <p className="text-white/50 text-xs font-mono uppercase mb-1">Template</p>
                <p className="text-white font-mono text-sm">{sendTestModal.name}</p>
              </div>
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-2">Send to Email</label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your@email.com"
                  autoFocus
                  data-testid="template-test-email-input"
                  className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleSendTest}
                  disabled={!testEmail || sending}
                  data-testid="template-test-email-send"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#D4AF37] text-black font-mono text-sm font-bold hover:bg-[#D4AF37]/90 transition-colors disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  {sending ? 'Sending...' : 'Send Test'}
                </button>
                <button
                  onClick={() => { setSendTestModal(null); setTestEmail(''); }}
                  className="px-4 py-3 border border-white/20 text-white/70 font-mono text-sm hover:bg-white/5"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplatesTab;
